import { Button } from 'antd';
import dayjs from 'dayjs';
import { createElement } from 'react';
import { formatPayeeInfo } from '@/common/utils/payee-info';

const dateFieldFormatMap = {
  createdAt: 'YYYY-MM-DD HH:mm:ss',
  loanDate: 'YYYY-MM-DD',
  paidAt: 'YYYY-MM-DD',
  repaymentDate: 'YYYY-MM-DD',
  requisitionDate: 'YYYY-MM-DD',
  rentEndedAt: 'YYYY-MM-DD',
  rentStartedAt: 'YYYY-MM-DD',
  submittedAt: 'YYYY-MM-DD',
  updatedAt: 'YYYY-MM-DD HH:mm:ss',
};

const documentTypeLabelMap = {
  expense: '费用',
  loan: '借款单',
  requisition: '申请单',
};

const sourceCollectionTypeLabelMap = {
  'feishu-bitable-operation-expense': '飞书多维表格',
  'hose-expense': '合思',
  'hose-loan': '合思',
  'hose-payment': '合思',
  'hose-requisition': '合思',
};

const approvalStatusLabelMap = {
  archived: '已归档',
  paid: '已完成',
  rejected: '已驳回',
};

const eventGrainLabelMap = {
  apportion: '分摊',
  detail: '明细',
  document: '整单',
  quota: '额度',
};

const paymentMethodLabelMap = {
  CITIC: 'CITIC',
  ICBCPAY: 'ICBCPAY',
  OFFLINE: '线下支付',
};

const formatPaymentMethod = (value) => paymentMethodLabelMap[value] || value;

const valueLabelMap = {
  approvalStatus: approvalStatusLabelMap,
  documentType: documentTypeLabelMap,
  eventGrain: eventGrainLabelMap,
  paymentMethod: paymentMethodLabelMap,
  sourceCollectionType: sourceCollectionTypeLabelMap,
};

const searchableFields = new Set([
  'approvalStatus',
  'departmentLevel2',
  'departmentLevel3',
  'documentNo',
  'documentType',
  'expenseDepartment',
  'expenseTypeLevel1',
  'expenseTypeLevel2',
  'expenseTypeLevel3',
  'invoiceStatus',
  'paymentMethod',
  'submitter',
  'title',
]);

const ellipsisFields = new Set(['description', 'documentDetail', 'payeeInfo', 'title']);
const hiddenTableFields = new Set(['apportionId', 'detailId', 'documentDetail', 'sourceEventKey']);

const fieldWidthMap = {
  apportionId: 180,
  createdAt: 180,
  description: 240,
  documentDetail: 240,
  documentId: 180,
  documentNo: 160,
  expenseDepartment: 160,
  id: 240,
  loanDate: 120,
  paidAt: 120,
  eventAmountCent: 140,
  eventAmountText: 140,
  payeeInfo: 200,
  rentEndedAt: 140,
  rentStartedAt: 140,
  repaymentDate: 140,
  requisitionDate: 120,
  submittedAt: 120,
  sourceAmountCent: 140,
  sourceAmountText: 140,
  sourceCollectionType: 140,
  sourceEventKey: 220,
  title: 220,
  updatedAt: 180,
};

export const expenseEventVisibleFields = [
  { dataIndex: 'id', title: '事件ID' },
  { dataIndex: 'operationEntityCode', title: '经营实体编码' },
  { dataIndex: 'sourceDepartmentId', title: '来源部门 ID' },
  {
    dataIndex: 'sourceCollectionType',
    title: '数据来源',
    valueEnum: sourceCollectionTypeLabelMap,
  },
  {
    dataIndex: 'documentType',
    title: '费用类型',
    valueEnum: {
      expense: '费用',
      loan: '借款单',
      requisition: '申请单',
    },
  },
  {
    dataIndex: 'eventGrain',
    title: '事件粒度',
    valueEnum: {
      apportion: '分摊',
      detail: '明细',
      document: '整单',
      quota: '额度',
    },
  },
  { dataIndex: 'title', title: '标题' },
  { dataIndex: 'documentNo', title: '单号' },
  { dataIndex: 'documentTemplate', title: '单据模版' },
  { dataIndex: 'payeeInfo', title: '收款信息', renderText: formatPayeeInfo },
  { dataIndex: 'invoiceStatus', title: '有无发票' },
  { dataIndex: 'departmentLevel2', title: '2级部门' },
  { dataIndex: 'departmentLevel3', title: '3级部门' },
  { dataIndex: 'expenseDepartment', title: '费用承担部门' },
  { dataIndex: 'submitterDepartment', title: '提单部门' },
  { dataIndex: 'submitter', title: '提交人' },
  { dataIndex: 'submittedAt', title: '提交日期', valueType: 'date' },
  { dataIndex: 'requisitionDate', title: '申请日期', valueType: 'date' },
  { dataIndex: 'approvalStatus', title: '审批状态', valueEnum: approvalStatusLabelMap },
  { dataIndex: 'expenseTypeLevel1', title: '1级费用类型' },
  { dataIndex: 'expenseTypeLevel2', title: '2级费用类型' },
  { dataIndex: 'expenseTypeLevel3', title: '3级费用类型' },
  { dataIndex: 'sourceAmountText', title: '来源金额' },
  { dataIndex: 'sourceAmountCent', title: '来源金额分', valueType: 'digit' },
  { dataIndex: 'eventAmountText', title: '支付金额' },
  { dataIndex: 'eventAmountCent', title: '支付金额分', valueType: 'digit' },
  { dataIndex: 'paidAt', title: '支付日期', valueType: 'date' },
  { dataIndex: 'rentStartedAt', title: '租金日期-起', valueType: 'date' },
  { dataIndex: 'rentEndedAt', title: '租金日期-止', valueType: 'date' },
  { dataIndex: 'loanDate', title: '借款日期', valueType: 'date' },
  { dataIndex: 'repaymentDate', title: '预计还款日期', valueType: 'date' },
  { dataIndex: 'loanDepartment', title: '借款部门' },
  { dataIndex: 'description', title: '描述' },
  {
    dataIndex: 'paymentMethod',
    title: '支付方式',
    renderText: formatPaymentMethod,
    valueEnum: paymentMethodLabelMap,
  },
  { dataIndex: 'payerAccountName', title: '付款开户名称' },
  { dataIndex: 'payerBankBranch', title: '付款开户网点' },
  { dataIndex: 'payerBankName', title: '付款银行' },
  { dataIndex: 'documentId', title: '单据详情' },
  { dataIndex: 'sourceEventKey', title: '来源事件标识' },
  { dataIndex: 'detailId', title: '来源明细ID' },
  { dataIndex: 'apportionId', title: '来源分摊ID' },
  { dataIndex: 'documentDetail', title: '来源明细快照' },
  { dataIndex: 'createdAt', title: '日志创建时间', valueType: 'dateTime' },
  { dataIndex: 'updatedAt', title: '日志更新时间', valueType: 'dateTime' },
];

const getSearchConfig = (dataIndex) => {
  if (searchableFields.has(dataIndex)) {
    return undefined;
  }
  return false;
};

const expenseEventDataColumns = expenseEventVisibleFields
  .filter((field) => !hiddenTableFields.has(field.dataIndex))
  .map((field) => ({
    ...field,
    ellipsis: ellipsisFields.has(field.dataIndex) || undefined,
    search: getSearchConfig(field.dataIndex),
    width: fieldWidthMap[field.dataIndex] || 140,
  }));

export const createPaidAtRangeColumn = () => ({
  title: '支付日期',
  dataIndex: 'paidAtRange',
  valueType: 'dateRange',
  hideInTable: true,
  search: {
    transform: (value) => {
      const range = Array.isArray(value) ? value : [];
      const start = range[0] ? dayjs(range[0]) : null;
      const end = range[1] ? dayjs(range[1]) : null;
      return {
        paidAtStart: start?.isValid() ? start.format('YYYY-MM-DD') : undefined,
        paidAtEnd: end?.isValid() ? end.format('YYYY-MM-DD') : undefined,
      };
    },
  },
});

export const createExpenseEventActionColumn = (onViewDetail) => ({
  title: '操作',
  valueType: 'option',
  fixed: 'right',
  width: 96,
  render: (_, record) => [
    createElement(
      Button,
      {
        key: 'detail',
        onClick: () => onViewDetail(record),
        type: 'link',
      },
      '查看详情',
    ),
  ],
});

export const createExpenseEventTableColumns = (onViewDetail) => [
  createPaidAtRangeColumn(),
  ...expenseEventDataColumns,
  createExpenseEventActionColumn(onViewDetail),
];

export const expenseEventTableColumns = [
  ...expenseEventDataColumns,
  createExpenseEventActionColumn(() => {}),
];

export const formatExpenseEventDetailValue = (field, value) => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  const labels = valueLabelMap[field.dataIndex];
  if (labels) {
    return labels[value] || value;
  }
  if (field.dataIndex === 'payeeInfo') {
    return formatPayeeInfo(value);
  }
  const format = dateFieldFormatMap[field.dataIndex];
  if (!format) {
    return value;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(format) : value;
};

export const expenseEventDetailItems = (event) =>
  expenseEventVisibleFields.map((field) => ({
    key: field.dataIndex,
    label: field.title,
    children: formatExpenseEventDetailValue(field, event?.[field.dataIndex]),
  }));
